import { useEffect, useMemo, useState } from 'react';

import { getTaskTimeRange } from '@/features/tasks/mobile-calendar/utils/timeRange';
import { cn } from '@/shared/lib/cn';
import type { Task } from '@/shared/types/task';
import { Button } from '@/shared/ui/Button';

type SlotDuration = 30 | 60;
type TimeRange = { start: Date; end: Date };

interface SlotsPanelProps {
  onSelectSlot: (slot: string, duration: SlotDuration) => void;
  tasks: Task[];
  selectedDate: Date;
  selectedSlot?: string | null;
  isLoading?: boolean;
}

const STEP_MINUTES = 30;
const WORK_START_HOUR = 9;
const WORK_END_HOUR = 21;
const MS_PER_MINUTE = 60_000;
const durations: SlotDuration[] = [30, 60];

function startOfDay(date: Date) {
  const day = new Date(date);
  day.setHours(0, 0, 0, 0);
  return day;
}

function isSameCalendarDay(a: Date, b: Date) {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function clampRange(range: TimeRange, min: Date, max: Date): TimeRange | null {
  const start = new Date(Math.max(range.start.getTime(), min.getTime()));
  const end = new Date(Math.min(range.end.getTime(), max.getTime()));

  return start < end ? { start, end } : null;
}

function ceilToStep(date: Date, stepMinutes: number, baseDay: Date) {
  const minutes = date.getHours() * 60 + date.getMinutes();
  const roundedMinutes = Math.ceil(minutes / stepMinutes) * stepMinutes;

  return new Date(baseDay.getTime() + roundedMinutes * MS_PER_MINUTE);
}

function formatTime(date: Date) {
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes}`;
}

function mergeBusyRanges(ranges: TimeRange[]) {
  const merged: TimeRange[] = [];

  for (const range of ranges) {
    const last = merged[merged.length - 1];

    if (!last || range.start > last.end) {
      merged.push({ start: new Date(range.start), end: new Date(range.end) });
      continue;
    }

    if (range.end > last.end) {
      last.end = new Date(range.end);
    }
  }

  return merged;
}

function getFreeRanges(
  windowStart: Date,
  windowEnd: Date,
  busyRanges: TimeRange[]
) {
  const freeRanges: TimeRange[] = [];
  let cursor = new Date(windowStart);

  for (const range of busyRanges) {
    if (range.start > cursor) {
      freeRanges.push({ start: new Date(cursor), end: new Date(range.start) });
    }

    if (range.end > cursor) {
      cursor = new Date(range.end);
    }
  }

  if (cursor < windowEnd) {
    freeRanges.push({ start: new Date(cursor), end: new Date(windowEnd) });
  }

  return freeRanges;
}

function buildSlots(
  freeRanges: TimeRange[],
  duration: SlotDuration,
  baseDay: Date,
  windowStart: Date
) {
  const slots: string[] = [];
  const durationMs = duration * MS_PER_MINUTE;
  const stepMs = STEP_MINUTES * MS_PER_MINUTE;

  for (const range of freeRanges) {
    let start = ceilToStep(range.start, STEP_MINUTES, baseDay);

    if (start < windowStart) {
      start = ceilToStep(windowStart, STEP_MINUTES, baseDay);
    }

    while (start.getTime() + durationMs <= range.end.getTime()) {
      const end = new Date(start.getTime() + durationMs);
      slots.push(`${formatTime(start)} - ${formatTime(end)}`);
      start = new Date(start.getTime() + stepMs);
    }
  }

  return slots;
}

export function SlotsPanel({
  onSelectSlot,
  tasks,
  selectedDate,
  selectedSlot,
  isLoading,
}: SlotsPanelProps) {
  const [duration, setDuration] = useState<SlotDuration | null>(60);
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!selectedSlot) {
      setIsCollapsed(false);
    }
  }, [selectedSlot]);

  const activeSlots = useMemo(() => {
    if (!duration || isLoading) {
      return [];
    }

    const now = new Date();
    const baseDay = startOfDay(selectedDate);
    const isToday = isSameCalendarDay(baseDay, startOfDay(now));

    const workStart = new Date(baseDay);
    workStart.setHours(WORK_START_HOUR, 0, 0, 0);

    const workEnd = new Date(baseDay);
    workEnd.setHours(WORK_END_HOUR, 0, 0, 0);

    const roundedNow = ceilToStep(now, STEP_MINUTES, baseDay);
    const windowStart =
      isToday && roundedNow > workStart ? roundedNow : workStart;

    if (windowStart >= workEnd) {
      return [];
    }

    const busyRanges = (tasks ?? [])
      .map((task) => getTaskTimeRange(task))
      .filter(Boolean)
      .map((range) => clampRange(range as TimeRange, windowStart, workEnd))
      .filter(Boolean) as TimeRange[];

    busyRanges.sort((a, b) => a.start.getTime() - b.start.getTime());

    return buildSlots(
      getFreeRanges(windowStart, workEnd, mergeBusyRanges(busyRanges)),
      duration,
      baseDay,
      windowStart
    );
  }, [duration, isLoading, selectedDate, tasks]);

  return (
    <>
      <div className="my-5 flex justify-center gap-2 font-semibold">
        {durations.map((value) => (
          <Button
            key={value}
            type="button"
            variant={duration === value ? 'secondary' : 'ghost'}
            size="lg"
            className={cn(
              'px-4',
              duration === value
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            onClick={() => {
              setDuration(duration === value ? null : value);
              setIsCollapsed(false);
            }}
          >
            {value} мин
          </Button>
        ))}
      </div>

      {duration && activeSlots.length > 0 && (
        <div
          className={cn(
            'grid gap-3',
            isCollapsed && selectedSlot ? 'grid-cols-1' : 'grid-cols-2'
          )}
        >
          {(isCollapsed && selectedSlot ? [selectedSlot] : activeSlots).map(
            (slot) => (
              <Button
                key={slot}
                type="button"
                variant={selectedSlot === slot ? 'secondary' : 'outline'}
                size="lg"
                className={cn(
                  'text-base font-medium',
                  selectedSlot === slot
                    ? 'border-primary bg-primary text-primary-foreground hover:bg-primary/90'
                    : 'border-border bg-background text-foreground hover:bg-muted'
                )}
                onClick={() => {
                  if (isCollapsed && selectedSlot === slot) {
                    setIsCollapsed(false);
                    return;
                  }

                  onSelectSlot(slot, duration);
                  setIsCollapsed(true);
                }}
              >
                {slot}
              </Button>
            )
          )}
        </div>
      )}

      {duration && activeSlots.length === 0 && (
        <p className="text-center py-6">Нет свободных слотов</p>
      )}
    </>
  );
}
